// lib/services/capture/device_info.ts
//
// Pure, dependency-free helpers for collecting and formatting device-model
// diagnostics from the browser. Used at startup/discovery to help identify the
// physical device (e.g. which phone model is running the app) so camera
// selection quirks can be correlated with a model.
//
// IMPORTANT PRIVACY CAVEAT: modern browsers deliberately reduce/omit the
// User-Agent and gate the precise model behind User-Agent Client Hints
// `getHighEntropyValues`, which many browsers do not implement at all (Safari,
// Firefox) and which can return an empty/generic model even when implemented.
// So the exact commercial model name (for example "HUAWEI Mate XTs Ultimate
// Design") frequently CANNOT be recovered. These helpers report what the
// platform exposes and clearly flag when the exact model is unavailable — they
// never assert a specific model the browser did not provide.
//
// All functions take injected navigator/screen-like objects rather than reading
// globals, so they are safe to unit-test in isolation and SSR-safe.

export interface UABrand {
  brand: string;
  version: string;
}

export interface UADataLike {
  brands?: UABrand[];
  mobile?: boolean;
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<Record<string, unknown>>;
}

export interface NavigatorLike {
  userAgent?: string;
  platform?: string;
  vendor?: string;
  language?: string;
  languages?: readonly string[];
  maxTouchPoints?: number;
  userAgentData?: UADataLike;
}

export interface ScreenLike {
  width?: number;
  height?: number;
}

export interface BasicDeviceInfo {
  user_agent: string | null;
  platform: string | null;
  vendor: string | null;
  language: string | null;
  languages: string[];
  max_touch_points: number | null;
  screen_width: number | null;
  screen_height: number | null;
  device_pixel_ratio: number | null;
  // Low-entropy UA Client Hints (available synchronously when supported).
  ua_low_entropy: {
    brands: UABrand[];
    mobile: boolean | null;
    platform: string | null;
  } | null;
}

export interface HighEntropyInfo {
  // Whether getHighEntropyValues was even callable in this browser.
  supported: boolean;
  model: string | null;
  platform: string | null;
  platform_version: string | null;
  architecture: string | null;
  bitness: string | null;
  mobile: boolean | null;
  full_version_list: UABrand[] | null;
  // Populated if the call throws / rejects.
  error: string | null;
}

export interface DerivedModel {
  // The exact model string ONLY when the platform actually reported a
  // non-empty one; otherwise null.
  model: string | null;
  // Human-readable explanation of availability — always set.
  note: string;
}

// Safe, non-fingerprinting-sensitive high-entropy hints we request. (We do not
// request the full set; only fields useful for identifying a device model and
// its OS/CPU.)
export const HIGH_ENTROPY_HINTS = [
  'model',
  'platform',
  'platformVersion',
  'architecture',
  'bitness',
  'mobile',
  'fullVersionList',
] as const;

function str_or_null(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function num_or_null(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function brands_or_null(v: unknown): UABrand[] | null {
  if (!Array.isArray(v)) return null;
  const out: UABrand[] = [];
  for (const b of v) {
    if (b && typeof b === 'object') {
      const o = b as Record<string, unknown>;
      const brand = str_or_null(o.brand);
      const version = str_or_null(o.version);
      if (brand) out.push({ brand, version: version ?? '' });
    }
  }
  return out;
}

// Synchronous snapshot of what the navigator/screen expose without any async
// permission-like call. Fully defensive against missing fields.
export function collect_basic_device_info(
  nav: NavigatorLike | null | undefined,
  screen?: ScreenLike | null,
  device_pixel_ratio?: number | null
): BasicDeviceInfo {
  const n = nav ?? {};
  const uad = n.userAgentData;

  return {
    user_agent: str_or_null(n.userAgent),
    platform: str_or_null(n.platform),
    vendor: str_or_null(n.vendor),
    language: str_or_null(n.language),
    languages: Array.isArray(n.languages) ? n.languages.filter((l) => typeof l === 'string') : [],
    max_touch_points: num_or_null(n.maxTouchPoints),
    screen_width: num_or_null(screen?.width),
    screen_height: num_or_null(screen?.height),
    device_pixel_ratio: num_or_null(device_pixel_ratio),
    ua_low_entropy: uad
      ? {
          brands: brands_or_null(uad.brands) ?? [],
          mobile: typeof uad.mobile === 'boolean' ? uad.mobile : null,
          platform: str_or_null(uad.platform),
        }
      : null,
  };
}

// Async: ask UA Client Hints for the higher-entropy fields (incl. model). This
// is NOT a permission prompt — it resolves silently — but many browsers do not
// implement it, in which case `supported` is false and everything is null.
export async function collect_high_entropy(
  nav: NavigatorLike | null | undefined
): Promise<HighEntropyInfo> {
  const empty: HighEntropyInfo = {
    supported: false,
    model: null,
    platform: null,
    platform_version: null,
    architecture: null,
    bitness: null,
    mobile: null,
    full_version_list: null,
    error: null,
  };

  const uad = nav?.userAgentData;
  if (!uad || typeof uad.getHighEntropyValues !== 'function') {
    return empty;
  }

  try {
    const v = await uad.getHighEntropyValues([...HIGH_ENTROPY_HINTS]);
    return {
      supported: true,
      model: str_or_null(v.model),
      platform: str_or_null(v.platform),
      platform_version: str_or_null(v.platformVersion),
      architecture: str_or_null(v.architecture),
      bitness: str_or_null(v.bitness),
      mobile: typeof v.mobile === 'boolean' ? v.mobile : null,
      full_version_list: brands_or_null(v.fullVersionList),
      error: null,
    };
  } catch (err) {
    return {
      ...empty,
      supported: true,
      error: err instanceof Error ? `${err.name}: ${err.message}` : String(err),
    };
  }
}

// Decide whether we actually have an exact model, and produce an honest note.
// Never claims a model the browser did not report.
export function derive_model(
  basic: BasicDeviceInfo,
  high: HighEntropyInfo
): DerivedModel {
  if (high.model) {
    return {
      model: high.model,
      note:
        'exact model reported via UA Client Hints (may still be generic, ' +
        'localized, or spoofed — treat as a hint, not proof)',
    };
  }

  // No model. Explain why, honestly.
  if (!high.supported) {
    return {
      model: null,
      note:
        'exact device model unavailable: this browser does not implement ' +
        'UA-Client-Hints getHighEntropyValues (e.g. Safari/Firefox). A ' +
        'specific model such as "HUAWEI Mate XTs Ultimate Design" cannot be ' +
        'confirmed from the browser here.',
    };
  }

  if (high.error) {
    return {
      model: null,
      note: `exact device model unavailable: getHighEntropyValues failed (${high.error}).`,
    };
  }

  return {
    model: null,
    note:
      'exact device model unavailable: UA Client Hints returned no model ' +
      '(browser privacy / UA reduction). A specific model such as "HUAWEI ' +
      'Mate XTs Ultimate Design" cannot be confirmed from the browser here.',
  };
}

function fmt_brands(brands: UABrand[] | null): string {
  if (!brands || brands.length === 0) return '(none)';
  return brands.map((b) => `${b.brand} ${b.version}`.trim()).join(', ');
}

// Compact, one-field-per-line output suitable for the in-app console on a
// narrow mobile screen. Never dumps large nested objects.
export function format_device_info_lines(
  basic: BasicDeviceInfo,
  high: HighEntropyInfo,
  derived: DerivedModel
): string[] {
  const out: string[] = [];
  out.push('--- device info ---');
  out.push(`userAgent=${basic.user_agent ?? '(none)'}`);
  out.push(`platform=${basic.platform ?? '(none)'} vendor=${basic.vendor ?? '(none)'}`);
  out.push(
    `language=${basic.language ?? '(none)'} languages=[${basic.languages.join(', ')}]`
  );
  out.push(
    `touchPoints=${basic.max_touch_points ?? '?'} ` +
      `screen=${basic.screen_width ?? '?'}x${basic.screen_height ?? '?'} ` +
      `dpr=${basic.device_pixel_ratio ?? '?'}`
  );

  if (basic.ua_low_entropy) {
    out.push(
      `uaHints(low): mobile=${basic.ua_low_entropy.mobile ?? '?'} ` +
        `platform=${basic.ua_low_entropy.platform ?? '(none)'} ` +
        `brands=[${fmt_brands(basic.ua_low_entropy.brands)}]`
    );
  } else {
    out.push('uaHints(low): not exposed by this browser');
  }

  if (high.supported && !high.error) {
    out.push(
      `uaHints(high): model=${high.model ?? '(empty)'} ` +
        `platform=${high.platform ?? '(none)'} ` +
        `platformVersion=${high.platform_version ?? '(none)'}`
    );
    out.push(
      `uaHints(high): arch=${high.architecture ?? '(none)'} ` +
        `bitness=${high.bitness ?? '(none)'} mobile=${high.mobile ?? '?'}`
    );
    out.push(`uaHints(high): fullVersionList=[${fmt_brands(high.full_version_list)}]`);
  } else if (high.error) {
    out.push(`uaHints(high): call failed — ${high.error}`);
  } else {
    out.push('uaHints(high): getHighEntropyValues unsupported in this browser');
  }

  out.push(`model: ${derived.model ?? '(unavailable)'}`);
  out.push(`note: ${derived.note}`);
  out.push('--- end device info ---');
  return out;
}
